import { useCallback, useEffect, useRef, useState } from 'react';
import { isAbort } from '../../Utils/api';

type Entry = { data: any; at: number };
const cache = new Map<string, Entry>();
const MAX_ENTRIES = 80;

const remember = (key: string, data: any) => {
  cache.set(key, { data, at: Date.now() });
  if (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
};

export const clearQueryCache = (prefix = '') => {
  Array.from(cache.keys()).forEach((key) => key.startsWith(prefix) && cache.delete(key));
};

/** Prefetches a query into the cache (used for the next page) without touching component state. */
export const prefetchQuery = (key: string, fetcher: (signal: AbortSignal) => Promise<any>, ttl = 30000) => {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < ttl) return;
  const controller = new AbortController();
  fetcher(controller.signal)
    .then((data) => remember(key, data))
    .catch(() => undefined);
};

/**
 * Fetches `key` with `fetcher`, aborting stale requests, keeping the previous data on screen while
 * the next page loads, and serving recently seen pages from memory (instant back and forth paging).
 */
export function useCachedQuery<T>(key: string | null, fetcher: (signal: AbortSignal) => Promise<T>, ttl = 30000) {
  const initial = key ? cache.get(key) : undefined;
  const [data, setData] = useState<T | null>(initial ? initial.data : null);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(!!key && !initial);
  const [reloadToken, setReloadToken] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!key) {
      setLoading(false);
      return;
    }
    const hit = cache.get(key);
    if (hit) {
      setData(hit.data);
      setError(null);
      if (Date.now() - hit.at < ttl) {
        setLoading(false);
        return;
      }
    }
    const controller = new AbortController();
    setLoading(true);
    fetcherRef
      .current(controller.signal)
      .then((result) => {
        remember(key, result);
        setData(result);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (isAbort(err)) return;
        setError(err);
        setLoading(false);
      });
    return () => controller.abort();
  }, [key, reloadToken, ttl]);

  const reload = useCallback(() => {
    if (key) cache.delete(key);
    setReloadToken((value) => value + 1);
  }, [key]);

  return { data, error, loading, reload };
}
